// Table view's own Meal Photos card — deliberately NOT the shared
// calendar/components/log-cards/LogMealPhotosCard.js (that one stays
// untouched, used as-is by the athlete/coach calendar Log view, capped to a
// 3-thumbnail flat preview). This is a Table-only variant: same carousel
// wiring and the same `log-card`/`log-card__*` classNames (so it inherits
// the existing dark theme from calendar-log.css without editing that file),
// but shows every photo grouped by meal type (Breakfast/Lunch/.../Snack)
// instead of a flat 3-photo cap — Table's expanded row has the room for it.
// Carousel open/close state now lives in the shared useMealPhotosCarousel
// hook (same one LogMealPhotosCard.js and DayDrawerPhotosCard.js use) — only
// the markup/photo-grouping stays isolated per view, not this wiring.
import PropTypes from "prop-types";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../../parts/SvgIcon";
import PhotosCarousel from "../../../metrics/meal-photos/components/PhotosCarousel";
import { useMealPhotosCarousel } from "../../../metrics/meal-photos/useMealPhotosCarousel";
import { mealOrderIndex } from "./viewTokens";

// Formats the UTC date into a readable format
const formatUtcDate = (utcDate) => {
  if (!utcDate) return "";
  const dateStr = utcDate.toString();
  if (dateStr.length !== 8) return "";
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const dayNum = dateStr.substring(6, 8);
  const date = moment(`${year}-${month}-${dayNum}`);
  return date.isValid() ? date.format("MMM D, YYYY") : "";
};

// Groups the photos by meal type
const groupByMealType = (photos) => {
  const byType = new Map();
  photos.forEach((photo) => {
    if (!byType.has(photo.mealType)) byType.set(photo.mealType, []);
    byType.get(photo.mealType).push(photo);
  });
  const orderedTypes = [...byType.keys()].sort((a, b) => mealOrderIndex(a) - mealOrderIndex(b));
  return orderedTypes.map((mealType) => [mealType, byType.get(mealType)]);
};

const TableMealPhotosPanel = ({ stat, utcDate, locals }) => {
  const { t } = useTranslation("athlete-portal");

  const allPhotos = useMemo(() => {
    return Object.entries(stat?.mealPhotos || {}).flatMap(([mealType, mealData]) =>
      (mealData.list || []).map((photo) => ({ ...photo, mealType, mealTags: mealData.tags || [] }))
    );
  }, [stat]);

  const { showCarousel, selectedPhotoIndex, setSelectedPhotoIndex, openCarousel, closeCarousel } =
    useMealPhotosCarousel({ allPhotos, dailyStatId: stat?._id });

  if (allPhotos.length === 0) return null;

  return (
    <>
      <div className="log-card log-card--meal-photos">
        <div className="log-card__header">
          <SvgIcon src="/img/new-icons/meal.svg" useCurrentColor={true} />
          {t("calendar.cards.mealPhotos.title")}
          <span className="np-table-detail-photo-count">
            {t("nutritionPage.progress.table.detail.photoCount", { count: allPhotos.length })}
          </span>
        </div>
        <div className="np-table-detail-photo-groups">
          {groupByMealType(allPhotos).map(([mealType, photos]) => (
            <div className="np-table-detail-photo-group" key={mealType}>
              <span className="np-table-detail-photo-group-label">{mealType}</span>
              <div className="log-card__photos">
                {photos.map((photo) => {
                  const src = photo.image?.s3Path || photo.s3Path || photo.url;
                  return (
                    <button
                      key={photo._id}
                      type="button"
                      className="log-card__photo-link"
                      onClick={() => openCarousel(photo._id)}
                      aria-label={t("calendar.cards.mealPhotos.viewAria")}
                    >
                      <img src={src} alt={t("calendar.cards.mealPhotos.alt")} className="log-card__photo-thumb" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PhotosCarousel
        photos={allPhotos}
        selectedPhotoIndex={selectedPhotoIndex}
        isOpen={showCarousel}
        onClose={closeCarousel}
        onPhotoChange={setSelectedPhotoIndex}
        date={formatUtcDate(utcDate)}
        locals={locals}
        utcDate={utcDate}
      />
    </>
  );
};

TableMealPhotosPanel.propTypes = {
  stat: PropTypes.object.isRequired,
  utcDate: PropTypes.number,
  locals: PropTypes.object,
};

export default TableMealPhotosPanel;
